/**
 * Wire schemas for the staff-facing care-team-assignment surface (`BACKLOG.md` X20,
 * `docs/decisions/care-team-assignment-scope.md`).
 *
 * Before this surface existed, no route or UI anywhere ever wrote a `care_team_memberships` row —
 * the table `shared/src/careTeam.ts`'s `decideChartAccess` depends on for every clinical-access
 * decision had no writer at all outside a direct-DB-insert test helper. This is that writer: an
 * explicit staff action that establishes (or ends) one user's relationship to one patient. See the
 * decision doc for why this is the *only* mechanism built for X20 — deliberately not an automatic
 * derivation from appointment booking or check-in.
 *
 * `relationship` on the request side is `ASSIGNABLE_CARE_TEAM_RELATIONSHIPS`
 * (`careTeam.ts`) — never `'none'`, which is a read-side fail-closed default, not a value a route
 * should ever accept from a caller.
 */

import { z } from 'zod';

import { ASSIGNABLE_CARE_TEAM_RELATIONSHIPS } from './careTeam.ts';

export const assignableCareTeamRelationshipSchema = z.enum(ASSIGNABLE_CARE_TEAM_RELATIONSHIPS);

/**
 * `startedAt` is optional and, when given, must not be in the future — a relationship is established
 * as of now or as of a real past moment (e.g. recording a covering assignment that already began this
 * morning before anyone got to a keyboard), never scheduled ahead of time. Omitted means "now",
 * resolved server-side (`claude-kit/rules/invariants.md`'s "server-authoritative values never come
 * from the request" governs the *default*, not this bound — a caller-supplied past timestamp is an
 * ordinary, legitimate input here, the same way `encounterRequests.ts`'s own occurredAt fields accept
 * a caller-supplied past moment).
 */
export const assignCareTeamMemberRequestSchema = z.strictObject({
  userId: z.uuid(),
  relationship: assignableCareTeamRelationshipSchema,
  startedAt: z.iso.datetime({ offset: true }).optional(),
});
export type AssignCareTeamMemberRequest = z.infer<typeof assignCareTeamMemberRequestSchema>;

export const careTeamMembershipResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  userId: z.uuid(),
  userEmail: z.string(),
  userRole: z.string(),
  relationship: z.string(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type CareTeamMembershipResponse = z.infer<typeof careTeamMembershipResponseSchema>;

export const assignCareTeamMemberResponseSchema = z.strictObject({
  membership: careTeamMembershipResponseSchema,
});
export type AssignCareTeamMemberResponse = z.infer<typeof assignCareTeamMemberResponseSchema>;

export const listCareTeamMembershipsResponseSchema = z.strictObject({
  members: z.array(careTeamMembershipResponseSchema),
});
export type ListCareTeamMembershipsResponse = z.infer<typeof listCareTeamMembershipsResponseSchema>;

export const endCareTeamMembershipResponseSchema = z.strictObject({
  membership: careTeamMembershipResponseSchema,
});
export type EndCareTeamMembershipResponse = z.infer<typeof endCareTeamMembershipResponseSchema>;

/**
 * `member_not_found` — `userId` names no active member of the caller's own org (distinct from
 * `not_found`, which is the *patient* not existing, matching `parsePatientId`'s own 404 for that).
 * `relationship_not_allowed_for_role` — the target member's role can never hold this relationship
 * (`isTreatmentRelationshipForRole`) — e.g. assigning `'attending'` to a `front_desk` member, which
 * `decideChartAccess`'s own `TREATMENT_RELATIONSHIPS_BY_ROLE` would silently never honour anyway; a
 * route accepting it would produce a row that reads as an active membership and grants nothing.
 * `already_active` — an active (non-ended, non-deleted) row already exists for this (patient, user)
 * pair; end it first (matching `access.ts`'s `resolveCareTeamRelationship` comment that today's
 * writes are expected to produce at most one active row per pair).
 */
export const CARE_TEAM_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'member_not_found',
  'relationship_not_allowed_for_role',
  'already_active',
  'not_active',
  'chart_access_denied',
  'audit_unavailable',
] as const;
export type CareTeamErrorCode = (typeof CARE_TEAM_ERROR_CODES)[number];
